/**
 * Keep the screen awake while the tuner is listening.
 *
 * Tuning is a two-handed job with both eyes on the meters and nothing touching
 * the screen, which is exactly when a phone decides to lock itself. The Screen
 * Wake Lock API exists for this; where it is missing, nothing changes.
 */

import { useEffect, useRef } from 'react'

/** The slice of the API this hook uses; `navigator.wakeLock` may be absent. */
interface WakeLockCapableNavigator {
  readonly wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinel>
  }
}

/** True when the browser offers screen wake locks at all. */
export function isWakeLockAvailable(): boolean {
  if (typeof navigator === 'undefined') return false
  return typeof (navigator as WakeLockCapableNavigator).wakeLock?.request === 'function'
}

/**
 * Hold a screen wake lock while `active` is true.
 *
 * The browser drops the lock whenever the page is hidden and never gives it
 * back on its own, so it is re-requested when the page becomes visible again.
 *
 * @param active - Whether the screen should be kept awake.
 */
export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null)

  useEffect(() => {
    if (!active || !isWakeLockAvailable()) return undefined

    // An effect that re-runs must not release a lock a later run has taken.
    let disposed = false

    const request = (): void => {
      if (disposed || sentinelRef.current !== null) return
      const { wakeLock } = navigator as WakeLockCapableNavigator
      if (!wakeLock) return

      wakeLock
        .request('screen')
        .then((sentinel) => {
          if (disposed) {
            void sentinel.release()
            return
          }
          sentinelRef.current = sentinel
          sentinel.addEventListener('release', () => {
            if (sentinelRef.current === sentinel) sentinelRef.current = null
          })
        })
        .catch((error: unknown) => {
          // Denied locks are ordinary: low battery, or a policy that forbids
          // them. The tuner works either way, so this is not worth a notice.
          console.warn('[multitimbral] screen wake lock refused', error)
        })
    }

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') request()
    }

    request()
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      disposed = true
      document.removeEventListener('visibilitychange', onVisibilityChange)
      const sentinel = sentinelRef.current
      sentinelRef.current = null
      void sentinel?.release().catch(() => {
        // Already released by the browser; nothing to do.
      })
    }
  }, [active])
}
